/**
 * `<NotesTab/>` — the drawer's Notes tab: a per-node feedback thread (B4.1).
 *
 * Issue #767 (Phase 4). Fills the seam the #760 placeholder left in
 * `NodeDetailDrawer`. Lightweight per-node feedback — context attached to the
 * work, not a chat channel (§A3 / B4.1). A thread of notes (newest-first, as
 * the endpoint returns), each with the author's avatar + name + an absolute
 * date and the note body; a compose box at the bottom (textarea + post).
 *
 * §A9 brand firewall: a note is collaboration / provenance, NEVER a scorecard
 * about a person. We render WHO wrote WHAT, WHEN — we never count, tally, rank,
 * or aggregate notes about a person. The "(2)" tab count is a count of notes on
 * THIS node (about the work), not about any person. The body is user content;
 * we add no evaluative chrome.
 *
 * The view is presentational over the `useNodeNotes` hook (data ⟂ view, §A7):
 * it receives the thread + states + the author/mention resolver and never calls
 * `apiFetch`. Author + mention names resolve through ONE place — the members
 * map handed down from the drawer (`useTaskTeamMembers`).
 *
 * @mention: a minimal, real picker. Typing `@` opens a teammate picker; picking
 * inserts `@Name` and records the membership id, which is sent in `mentions[]`
 * (this is what feeds the #765 digest's "notes that mention you"). We never
 * fake mentions — if nobody is `@`-referenced, `mentions` is empty.
 *
 * Delete: the affordance shows only on notes the actor may remove (their own,
 * or any if they're a lead) — mirroring the backend's 403 rule, so we never
 * paint a button the API would reject.
 */

import { type Ref, useEffect, useMemo, useRef, useState } from "react";

import type { TaskNote } from "../api/useNodeNotes";
import { formatEventStamp } from "../history/formatTime";
import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";

export interface NotesTabProps {
  /** The thread, newest-first (as the endpoint returns). */
  notes: TaskNote[];
  /** True while the thread is loading. */
  isLoading: boolean;
  /** True on a fetch failure (incl. a not-yet-deployed / ungated 404). */
  isError: boolean;
  /** The team roster — resolves author + mention ids → name / initials. */
  members: TaskMember[];
  /** The signed-in actor's membership id (null if unresolved) — drives delete. */
  currentMembershipId: string | null;
  /** Whether the actor is a lead / co_lead — a lead may remove any note. */
  isLead: boolean;
  /** Post a note (body + the mentioned membership ids). */
  onAdd: (body: string, mentions: string[]) => void;
  /** True while a post is in flight (disables the compose box). */
  isAdding: boolean;
  /** Remove a note by id (only ever called for a removable note). */
  onRemove: (noteId: string) => void;
  /**
   * The team's IANA timezone (e.g. `"Asia/Singapore"`) — note timestamps
   * render as date + HH:MM in this zone (#906). Optional; when omitted the
   * stamp falls back to the viewer's local zone.
   */
  teamTimezone?: string | undefined;
  /**
   * The per-node note-subscription toggle (#907). Rendered at the top of the
   * tab so the member can opt in to be notified of notes on THIS task. Omit the
   * whole object (e.g. when the subscription state hasn't been wired) to hide
   * the toggle entirely.
   */
  subscription?:
    | {
        /** Whether the caller is currently subscribed to this node's notes. */
        subscribed: boolean;
        /** True while the GET / PUT / DELETE is in flight (disables the toggle). */
        busy: boolean;
        /** Subscribe (true) / unsubscribe (false). */
        onToggle: (subscribe: boolean) => void;
      }
    | undefined;
  /**
   * A note id to scroll to + briefly highlight (#907 deep-link). When set, the
   * matching note flashes a transient teal ring; `onFocusConsumed` is called
   * once the highlight has been applied so the one-shot hint clears.
   */
  focusNoteId?: string | null | undefined;
  /** Called once the `focusNoteId` highlight has been applied (clears the hint). */
  onFocusConsumed?: (() => void) | undefined;
}

export function NotesTab({
  notes,
  isLoading,
  isError,
  members,
  currentMembershipId,
  isLead,
  onAdd,
  isAdding,
  onRemove,
  teamTimezone,
  subscription,
  focusNoteId,
  onFocusConsumed,
}: NotesTabProps) {
  const theme = useMapTheme();

  // O(1) author / mention resolution from the one roster source.
  const memberById = useMemo(() => new Map(members.map((m) => [m.membershipId, m])), [members]);

  // #907 deep-link: scroll the targeted note into view + flash a transient ring.
  // The ref is set by the matching `<NoteItem/>`; once the note is present in
  // the thread we scroll to it, mark it highlighted for a moment, then tell the
  // caller the one-shot hint is consumed so re-opening doesn't re-flash.
  const focusRef = useRef<HTMLLIElement | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  useEffect(() => {
    if (!focusNoteId) return;
    // Only act once the targeted note has actually rendered in the thread.
    if (!notes.some((n) => n.id === focusNoteId)) return;
    focusRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    setHighlightId(focusNoteId);
    onFocusConsumed?.();
    const t = setTimeout(() => setHighlightId(null), 2200);
    return () => clearTimeout(t);
  }, [focusNoteId, notes, onFocusConsumed]);

  return (
    <div className="flex h-full flex-col" data-testid="drawer-notes-tab">
      {subscription && (
        <NoteSubscriptionToggle
          subscribed={subscription.subscribed}
          busy={subscription.busy}
          onToggle={subscription.onToggle}
          theme={theme}
        />
      )}

      {/* The thread (scrolls); the compose box stays pinned below. */}
      <div className="min-h-0 flex-1 overflow-auto" data-testid="notes-thread">
        {isError ? (
          <NotesUnavailable theme={theme} />
        ) : isLoading ? (
          <NotesLoading theme={theme} />
        ) : notes.length === 0 ? (
          <NotesEmpty theme={theme} />
        ) : (
          <ul className="flex flex-col">
            {notes.map((note, i) => {
              const focused = note.id === focusNoteId || note.id === highlightId;
              return (
                <NoteItem
                  key={note.id}
                  note={note}
                  memberById={memberById}
                  isLast={i === notes.length - 1}
                  canRemove={canRemoveNote(note, currentMembershipId, isLead)}
                  onRemove={onRemove}
                  teamTimezone={teamTimezone}
                  highlighted={note.id === highlightId}
                  rowRef={focused ? focusRef : undefined}
                  theme={theme}
                />
              );
            })}
          </ul>
        )}
      </div>

      <ComposeBox members={members} disabled={isAdding} onPost={onAdd} theme={theme} />
    </div>
  );
}

// ── Per-node note-subscription toggle (#907) ─────────────────────────────────

/**
 * "Notify me of notes on this task" — the per-node opt-in (§A9: the member's
 * own choice, on their own terms). The two notification paths are easy to
 * conflate (#907 confusion), so ONE calm line distinguishes them: an @mention
 * ALWAYS reaches you; this toggle is the separate, optional choice to follow
 * EVERY note on this task. A plain checkbox-style switch, calm, no count /
 * tally framing.
 */
function NoteSubscriptionToggle({
  subscribed,
  busy,
  onToggle,
  theme,
}: {
  subscribed: boolean;
  busy: boolean;
  onToggle: (subscribe: boolean) => void;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <div
      data-testid="note-subscription"
      className="mb-3 rounded-lg border p-3"
      style={{
        borderColor: editorial ? c.rule : c.cardBorder,
        backgroundColor: editorial ? c.bgRecess : "#F7F8FA",
      }}
    >
      <label className="flex items-start gap-2.5">
        <input
          type="checkbox"
          data-testid="note-subscription-toggle"
          checked={subscribed}
          disabled={busy}
          onChange={(e) => onToggle(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 disabled:opacity-50"
          style={{ accentColor: editorial ? c.teal : "#1F7A6E" }}
          aria-label="Notify me of notes on this task"
        />
        <span className="min-w-0">
          <span className="block text-xs font-semibold" style={{ color: c.ink }}>
            Notify me of notes on this task
          </span>
          {/* #907 — distinguish the two paths in one calm line: an @mention
              always reaches you on its own; this toggle is the separate opt-in
              to follow every note here. */}
          <span
            className="mt-0.5 block text-[11px]"
            style={{ color: c.faint }}
            data-testid="note-subscription-help"
          >
            An @mention always notifies you. Turn this on to follow every note on this task.
          </span>
        </span>
      </label>
    </div>
  );
}

/**
 * May the actor remove this note? Author or a lead / co_lead only — mirrors the
 * backend's 403 rule so the UI never offers a delete the API would reject.
 */
export function canRemoveNote(
  note: TaskNote,
  currentMembershipId: string | null,
  isLead: boolean,
): boolean {
  if (isLead) return true;
  return currentMembershipId != null && note.author_membership_id === currentMembershipId;
}

// ── One note in the thread ───────────────────────────────────────────────────

function NoteItem({
  note,
  memberById,
  isLast,
  canRemove,
  onRemove,
  teamTimezone,
  highlighted,
  rowRef,
  theme,
}: {
  note: TaskNote;
  memberById: Map<string, TaskMember>;
  isLast: boolean;
  canRemove: boolean;
  onRemove: (noteId: string) => void;
  teamTimezone?: string | undefined;
  /** True while the #907 deep-link transient highlight is active on this note. */
  highlighted?: boolean;
  /** A ref the deep-link uses to scroll this note into view (#907). */
  rowRef?: Ref<HTMLLIElement> | undefined;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const author = note.author_membership_id ? memberById.get(note.author_membership_id) : undefined;
  const name = author?.name ?? "Former member";
  const initials = author?.initials ?? "·";

  return (
    <li
      ref={rowRef}
      data-testid="note-item"
      data-note-id={note.id}
      data-highlighted={highlighted ? "true" : "false"}
      className="flex gap-2.5 rounded-lg px-1.5 py-3 transition-shadow"
      style={{
        // Editorial: each note is a self-contained author + bubble block, so
        // the thread reads as a paper conversation — no row divider rule.
        ...(isLast || editorial ? {} : { borderBottom: `1px solid ${c.cardBorder}` }),
        ...(highlighted
          ? {
              boxShadow: `0 0 0 2px ${c.teal}`,
              backgroundColor: editorial ? c.tealSoft : "#ECF7F4",
            }
          : {}),
      }}
    >
      <NoteAvatar initials={initials} name={name} theme={theme} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className="truncate text-[11px] font-bold"
            style={{ color: c.ink }}
            data-testid="note-author"
          >
            {name}
          </span>
          <span
            className="shrink-0 text-[10px]"
            style={{
              color: c.faint,
              fontFamily: editorial ? theme.fonts.mono : undefined,
            }}
            data-testid="note-date"
          >
            {formatNoteDate(note.created_at, teamTimezone)}
          </span>
        </div>
        {/* Editorial: the body sits in a recessed (--bg-2) bubble with ink-2
            text — the B4.1 "context attached to the work" look. Legacy: a plain
            paragraph under the meta line (byte-identical). */}
        <p
          className={
            editorial
              ? "mt-1 whitespace-pre-wrap break-words rounded-md px-3 py-2.5 text-[13px] leading-relaxed"
              : "mt-0.5 whitespace-pre-wrap break-words text-xs"
          }
          style={editorial ? { color: c.slate, backgroundColor: c.bgRecess } : { color: c.slate }}
          data-testid="note-body"
        >
          {note.body}
        </p>
      </div>
      {canRemove && (
        <button
          type="button"
          data-testid="note-remove"
          aria-label="Remove this note"
          onClick={() => onRemove(note.id)}
          className="h-fit shrink-0 rounded p-0.5 text-xs leading-none transition-colors hover:bg-slate-100"
          style={{ color: c.faint }}
        >
          ✕
        </button>
      )}
    </li>
  );
}

function NoteAvatar({
  initials,
  name,
  theme,
}: { initials: string; name: string; theme: MapTheme }) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <span
      title={name}
      data-testid="note-avatar"
      className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[9px] font-bold"
      // Editorial avatars are a saturated fill with white initials (the Wave-1
      // `<Avatar/>` idiom); legacy keeps the soft-mint fill + teal-ink initials.
      style={{
        backgroundColor: c.avatarA,
        color: editorial ? "#FFFFFF" : c.tealInk,
      }}
    >
      {initials}
    </span>
  );
}

// ── Compose box (textarea + post + @mention) ─────────────────────────────────

function ComposeBox({
  members,
  disabled,
  onPost,
  theme,
}: {
  members: TaskMember[];
  disabled: boolean;
  onPost: (body: string, mentions: string[]) => void;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const [text, setText] = useState("");
  // membershipId → name for everyone the draft `@`-references. Re-derived
  // against the live text on post so deleting a mention from the text drops it.
  const [mentioned, setMentioned] = useState<Map<string, string>>(new Map());
  const [picking, setPicking] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  const trimmed = text.trim();
  const canPost = trimmed.length > 0 && !disabled;

  const handleChange = (next: string) => {
    setText(next);
    // Open the picker the moment a fresh `@` is typed at the end of the text
    // (a minimal, predictable trigger — no caret arithmetic).
    if (next.endsWith("@")) setPicking(true);
    else setPicking(false);
  };

  const pickMention = (m: TaskMember) => {
    // Replace the trailing `@` (if any) with `@Name ` and record the id.
    const base = text.endsWith("@") ? text.slice(0, -1) : text;
    setText(`${base}@${m.name} `);
    setMentioned((prev) => new Map(prev).set(m.membershipId, m.name));
    setPicking(false);
    taRef.current?.focus();
  };

  const post = () => {
    if (!canPost) return;
    // Only send mentions whose `@Name` token still survives in the final text
    // (so editing out a mention doesn't ghost-send it). WHO, never a count.
    const mentions = [...mentioned.entries()]
      .filter(([, name]) => text.includes(`@${name}`))
      .map(([id]) => id);
    onPost(trimmed, mentions);
    setText("");
    setMentioned(new Map());
    setPicking(false);
  };

  return (
    <div
      className="relative border-t pt-3"
      style={
        editorial
          ? {
              borderColor: c.rule,
              // The B4.1 composer sits on a recessed (--bg-2) footer; negative
              // margins let it span the tab content's horizontal padding.
              backgroundColor: c.bgRecess,
              marginLeft: -12,
              marginRight: -12,
              marginBottom: -12,
              paddingLeft: 12,
              paddingRight: 12,
              paddingBottom: 12,
            }
          : { borderColor: c.cardBorder }
      }
    >
      <div className={editorial ? "flex items-start gap-2.5" : undefined}>
        {/* Editorial: a small "you" pencil disc anchors the composer (the
            wireframe's leading avatar). We don't have the actor's identity here,
            so it's a neutral teal-soft glyph, not a fabricated member. */}
        {editorial && (
          <span
            aria-hidden
            className="mt-1 inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[11px]"
            style={{ backgroundColor: c.tealSoft, color: c.tealInk }}
          >
            ✎
          </span>
        )}
        <textarea
          ref={taRef}
          data-testid="note-compose"
          value={text}
          disabled={disabled}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => {
            // ⌘/Ctrl-Enter posts (a calm keyboard affordance; plain Enter keeps
            // newlines, since a note may be a few lines like the gallery's).
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              post();
            }
          }}
          placeholder="write a note…"
          rows={3}
          className="w-full resize-none rounded-lg border p-2.5 text-sm outline-none disabled:opacity-60"
          style={{
            borderColor: editorial ? c.rule2 : c.cardBorder,
            backgroundColor: editorial ? c.cardBg : "#F7F8FA",
            color: c.ink,
            fontFamily: editorial ? theme.fonts.sans : undefined,
          }}
        />
      </div>
      {picking && members.length > 0 && (
        <MentionPicker
          members={members}
          onPick={pickMention}
          onClose={() => setPicking(false)}
          theme={theme}
        />
      )}
      <div
        className={
          editorial
            ? "mt-2 flex items-center justify-between pl-[33px]"
            : "mt-2 flex items-center justify-between"
        }
      >
        <span
          className="text-[10px]"
          style={{
            color: c.faint,
            fontFamily: editorial ? theme.fonts.mono : undefined,
          }}
        >
          type @ to mention a teammate
        </span>
        <button
          type="button"
          data-testid="note-post"
          onClick={post}
          disabled={!canPost}
          className="rounded-md px-4 py-1.5 text-[11px] font-semibold transition-opacity hover:opacity-90 disabled:opacity-40"
          // Editorial: an ink `.t-btn` (cream label on warm near-black);
          // legacy: the shipped solid-teal post button (white label).
          style={{
            backgroundColor: editorial ? c.ink : c.teal,
            color: editorial ? c.canvas : "#FFFFFF",
          }}
        >
          post
        </button>
      </div>
    </div>
  );
}

/** A small dropdown of teammates to `@`-mention. */
function MentionPicker({
  members,
  onPick,
  onClose,
  theme,
}: {
  members: TaskMember[];
  onPick: (m: TaskMember) => void;
  onClose: () => void;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <>
      {/* Click-away scrim. */}
      <button
        type="button"
        aria-label="Close mention picker"
        onClick={onClose}
        className="fixed inset-0 z-30 cursor-default"
      />
      <div
        data-testid="mention-picker"
        className="absolute bottom-full left-0 z-40 mb-1 max-h-48 w-56 overflow-auto rounded-lg border p-1 shadow-xl"
        style={{ borderColor: editorial ? c.rule2 : c.softBorder, backgroundColor: c.cardBg }}
      >
        <div
          className="px-2 py-1 text-[11px]"
          style={{
            color: c.faint,
            fontFamily: editorial ? theme.fonts.mono : undefined,
          }}
        >
          mention a teammate
        </div>
        {members.map((m) => (
          <button
            key={m.membershipId}
            type="button"
            data-testid="mention-option"
            data-membership-id={m.membershipId}
            onClick={() => onPick(m)}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-slate-50"
            style={{ color: c.ink }}
          >
            <span
              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold"
              style={{
                backgroundColor: c.avatarA,
                color: editorial ? "#FFFFFF" : c.tealInk,
              }}
            >
              {m.initials}
            </span>
            <span className="truncate">{m.name}</span>
          </button>
        ))}
      </div>
    </>
  );
}

// ── States ───────────────────────────────────────────────────────────────────

function NotesEmpty({ theme }: { theme: MapTheme }) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <div
      data-testid="notes-empty"
      className="flex flex-col items-center justify-center gap-1 py-10 text-center"
    >
      <span className="text-xl" aria-hidden>
        💬
      </span>
      <div
        className="text-sm font-semibold"
        style={{
          color: c.slate,
          fontFamily: editorial ? theme.fonts.serif : undefined,
          fontStyle: editorial ? "italic" : undefined,
        }}
      >
        No notes yet
      </div>
      <div className="text-xs" style={{ color: c.faint }}>
        Add the first.
      </div>
    </div>
  );
}

function NotesLoading({ theme }: { theme: MapTheme }) {
  return (
    <div
      data-testid="notes-loading"
      className="py-10 text-center text-xs"
      style={{ color: theme.palette.faint }}
    >
      Loading notes…
    </div>
  );
}

function NotesUnavailable({ theme }: { theme: MapTheme }) {
  return (
    <div
      data-testid="notes-unavailable"
      className="flex flex-col items-center justify-center gap-1 py-10 text-center"
    >
      <span className="text-xl" aria-hidden>
        💬
      </span>
      <div className="text-sm" style={{ color: theme.palette.muted }}>
        Notes unavailable
      </div>
    </div>
  );
}

// ── Date formatting ───────────────────────────────────────────────────────────

/**
 * Format a note's `created_at` as an absolute date + minute-precision time in
 * the team's timezone (e.g. "May 21 · 09:14"), per #906. Provenance wants the
 * actual moment a note was written, not a date alone; rendering it in the
 * team's local zone keeps it unambiguous across viewer locations. Reuses the
 * history view's `formatEventStamp` so both surfaces format identically, and
 * degrades to an empty string on an unparseable value rather than throwing.
 */
function formatNoteDate(iso: string, tz?: string): string {
  return formatEventStamp(iso, tz);
}
